import { Link, useNavigate } from "react-router-dom";
import { RegisterPage, orNone, valueToLabel } from "./RegisterPage";
import type { ColumnDef, FieldDef } from "./RegisterPage";
import { sites } from "../../../lib/resources";
import type { Site } from "../../../lib/resources";
import { useCustomerFilter, CustomerFilterChip } from "../customerFilter";
import { StatusPill } from "../../../components/StatusPill";

const STATUS = [
  { value: "potential", label: "Potential" },
  { value: "active", label: "Active" },
  { value: "dead", label: "Dead" },
];

const columns: ColumnDef<Site>[] = [
  {
    key: "name",
    label: "Site",
    width: "23%",
    render: (s) => (
      <Link to={`/office/sites/${s.id}`} className="text-ele-green hover:underline">
        {s.name}
      </Link>
    ),
    sortValue: (s) => s.name ?? "",
  },
  {
    key: "customer",
    label: "Customer",
    width: "21%",
    // Forward click-through to the customer (Session ADI). The desktop rows here are not row-click
    // navigable (only the Site name cell is a link), so no stopPropagation is needed.
    render: (s) =>
      s.customer?.name ? (
        <Link to={`/office/customers/${encodeURIComponent(s.customerId)}`} className="text-ele-green hover:underline">
          {s.customer.name}
        </Link>
      ) : (
        orNone(s.customer?.name)
      ),
    sortValue: (s) => s.customer?.name ?? "",
  },
  { key: "address", label: "Address", width: "29%", render: (s) => s.address, sortValue: (s) => s.address ?? "" },
  { key: "status", label: "Status", width: "11%", render: (s) => valueToLabel(STATUS, s.status), sortValue: (s) => valueToLabel(STATUS, s.status) },
  { key: "lifts", label: "Lifts", width: "8%", render: (s) => s._count?.assets ?? 0, sortValue: (s) => s._count?.assets ?? 0 },
];

const emptyForm = {
  customerId: "",
  name: "",
  address: "",
  postcode: "",
  status: "potential",
  accessDetails: "",
  notes: "",
};

export function SitesPage() {
  const navigate = useNavigate();
  // Session ADI: when the customer detail page links here with ?customerId=, show only that
  // customer's sites (client-side filter over the loaded rows) plus a dismissable chip.
  const { customerId, clear } = useCustomerFilter();
  const fields: FieldDef[] = [
    {
      name: "customerId",
      label: "Customer",
      type: "record-picker",
      pickerKind: "customer",
      required: true,
    },
    { name: "name", label: "Site name", type: "text", required: true },
    { name: "address", label: "Site address", type: "text", required: true },
    { name: "postcode", label: "Postcode", type: "postcode-lookup", addressField: "address", help: "Type a UK postcode to look up the address, or enter it manually." },
    { name: "status", label: "Status", type: "select", options: STATUS },
    {
      name: "accessDetails",
      label: "Access details",
      type: "textarea",
      help: "Door codes, fob access, parking, key location, building hours, motor room access... Shown to the engineer before they attend (internal only, never to the customer).",
    },
    { name: "notes", label: "Notes", type: "textarea" },
  ];

  return (
    <RegisterPage<Site>
      title="Sites"
      subtitle="The buildings where the lifts are."
      noun="site"
      resource={sites}
      columns={columns}
      fields={fields}
      rowId={(s) => s.id}
      // Session SC2C: read 50 at a time; the SERVER applies the customer filter, the search and the sort (GET /api/sites).
      serverPaged
      exportList="sites"
      query={customerId ? { customerId } : undefined}
      toolbar={customerId ? <CustomerFilterChip noun="sites" onClear={clear} /> : undefined}
      toForm={(s) => ({
        customerId: s.customerId ?? "",
        name: s.name ?? "",
        address: s.address ?? "",
        postcode: s.postcode ?? "",
        status: s.status ?? "potential",
        accessDetails: s.accessDetails ?? "",
        notes: s.notes ?? "",
      })}
      emptyForm={emptyForm}
      // Session ACW (mobile usability): below md keep the identifier, the customer and the status
      // (the three things worth a glance on a phone); Address/Lifts count hide. The Edit/Delete
      // actions column is untouched by this (RegisterPage never hides it): editing a site happens
      // only via this inline form, there is no site-level edit/delete on the detail page, so those
      // controls must stay reachable on a phone.
      mobileColumns={["name", "customer", "status"]}
      mobileLabel="Sites"
      // Below md the sites list becomes two-line swipe rows (patch): site name on line 1,
      // customer + status pill on line 2, Edit/Delete revealed on swipe left, a tap opens the
      // site detail. Desktop (md and up) keeps the exact table above.
      mobileRow={(s) => ({
        title: s.name,
        subtitle: orNone(s.customer?.name),
        meta: <StatusPill domain="siteStatus" status={s.status} />,
        onTap: () => navigate(`/office/sites/${s.id}`),
      })}
      deleteConfirm={(s) => {
        const lifts = s._count?.assets ?? 0;
        const contracts = s._count?.contracts ?? 0;
        const liftWord = lifts === 1 ? "lift" : "lifts";
        const contractWord = contracts === 1 ? "contract" : "contracts";
        return {
          // Lists what will be deleted (lifts + contracts counts, and the records they carry).
          // Invoices are kept (detached, not deleted). A second click confirms; no typing.
          message: `This permanently deletes ${s.name} and everything attached to it: ${lifts} ${liftWord}, ${contracts} ${contractWord}, and all of their jobs, service records and defects. Invoices are kept in Xero. This cannot be undone.`,
          warning: contracts > 0 ? "This site has contracts on file." : undefined,
        };
      }}
    />
  );
}
